import {
  ALERT_BANDS,
  ALERT_MUTE_MAX_DAYS,
  ALERT_REVIEW_DAYS,
  ALERT_SNOOZE_MAX_DAYS,
  MONITOR_CATALOG,
  MONITOR_KEYS,
  TICKET_STATE_LABEL,
  localDateOf,
  type AlertBand,
  type AlertSubjectType,
  type MonitorHealth,
  type MonitorNotMonitoredReason,
  type TicketState,
} from '@servwell/shared';
import { dateOnly, dayInTz, timeInTz } from '../../lib/format';
import type { Tone } from '../ui/Badge';

/**
 * The words Settings → Alerts says (ops insights Phase 6b spec §6, grown by Phase 7 spec §7),
 * pure so each state is a test rather than a render. The card decides nothing: health comes
 * from the server (`monitorHealth` in `@servwell/shared`, the one rule the board reads too),
 * and so do the counts and the judgments.
 */

export const MONITORS_EXPLAINER =
  'Each monitor checks for one problem. In Testing, what it finds is shown only here, for you to judge; Live, it goes on the alerts board for you and the office.';

export interface HealthView {
  tone: Tone;
  badge: 'Monitored' | 'Not monitored';
  /** Why it is not monitored, in a sentence. Null when it is. */
  reason: string | null;
}

const REASON: Record<MonitorNotMonitoredReason, { tone: Tone; text: string }> = {
  never_ran: {
    tone: 'warn',
    text: 'Never ran. Checks run every hour from 7 AM to 7 PM, shop time.',
  },
  failed: { tone: 'danger', text: 'The last check failed' },
  overdue: { tone: 'warn', text: 'Overdue: no check has finished on time.' },
  disabled: { tone: 'neutral', text: 'Turned off.' },
};

/**
 * Monitored, or Not monitored and why. A failed check names its cause: the owner is comparing
 * these with reality, and "it failed" with nothing after it is a dead end.
 */
export function healthView(health: MonitorHealth): HealthView {
  if (health.state === 'monitored') {
    return { tone: 'success', badge: 'Monitored', reason: null };
  }
  const r = REASON[health.reason];
  const reason =
    health.reason === 'failed'
      ? health.cause
        ? `${r.text}: ${health.cause}`
        : `${r.text}.`
      : r.text;
  return { tone: r.tone, badge: 'Not monitored', reason };
}

/**
 * An instant on the SHOP's clock: the time today, the date and time on another day, and the year
 * too when it is not the shop's current year. A lapsed customer's last visit is a year ago by
 * definition: "Sep 30" alone reads as last month (p7 QA).
 */
export function shopTime(at: Date | string, tz: string, now: Date): string {
  const time = timeInTz(at, tz);
  if (dayInTz(at, tz) === dayInTz(now, tz)) return time;
  const sameYear =
    localDateOf(new Date(at), tz).slice(0, 4) === localDateOf(now, tz).slice(0, 4);
  const day = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(new Date(at));
  return `${day}, ${time}`;
}

export function lastCheckLine(at: Date | string | null, tz: string, now: Date): string {
  return at ? `Last check ${shopTime(at, tz, now)}` : 'No check yet';
}

export function liveCountLine(n: number): string {
  if (n === 0) return 'No live alerts';
  return n === 1 ? '1 live alert' : `${n.toLocaleString('en-US')} live alerts`;
}

/**
 * What a live list with nothing in it means. Only a monitored check's silence is an answer;
 * a monitor that never ran, failed or is overdue has not looked, and its empty list must not
 * read as an all-clear (spec: fail closed, "not monitored", never zero).
 */
export function emptyLiveLine(health: MonitorHealth): string {
  return health.state === 'monitored'
    ? 'Nothing live: the latest check found no problem.'
    : 'Nothing live, but this monitor is not being checked right now, so that is not an all-clear.';
}

// ── Settings → Alerts (Phase 7 spec §7) ─────────────────────────────────────────────────────

export const BAND_LABEL: Record<AlertBand, string> = {
  critical: 'Critical',
  high: 'High',
  normal: 'Normal',
};

/** The severity choices, the monitor's own band marked: choosing it clears the override. */
export function bandOptions(
  defaultBand: AlertBand,
): { value: AlertBand; label: string }[] {
  return ALERT_BANDS.map((b) => ({
    value: b,
    label: b === defaultBand ? `${BAND_LABEL[b]} (its own)` : BAND_LABEL[b],
  }));
}

/** What the owner chose, as a value for `monitor.update`: its own band is no override. */
export function bandOverrideFor(
  chosen: AlertBand,
  defaultBand: AlertBand,
): AlertBand | null {
  return chosen === defaultBand ? null : chosen;
}

export function firingNowLine(n: number): string {
  return n === 0 ? 'Nothing firing now' : `${n.toLocaleString('en-US')} firing now`;
}

/** A monitor's open alerts as the board would draw them Live (the server's count). */
export interface OpenOnBoard {
  shown: number;
  snoozed: number;
  muted: number;
}

const many = (n: number, one: string, more: string) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : more}`;

/**
 * What the Testing ↔ Live switch asks before it moves anything (spec §3: the open alerts move
 * with it, in the same save, and nobody is notified by the flip). EXACT about what the board
 * will draw: an alert under an active mute stays hidden, and a snoozed one shows only under
 * Snoozed, so neither is counted as becoming visible (the board's own predicates, counted by
 * the server).
 */
export function modeConfirm(
  label: string,
  to: 'testing' | 'live',
  open: OpenOnBoard,
): { question: string; consequence: string; action: string } {
  if (to === 'live') {
    const parts = [
      open.shown > 0
        ? `${many(open.shown, 'open alert', 'open alerts')} will show on the alerts board now, for you and the office to work`
        : null,
      open.snoozed > 0
        ? `${many(open.snoozed, 'snoozed alert shows', 'snoozed alerts show')} only under Snoozed until ${open.snoozed === 1 ? 'its' : 'their'} snooze ends`
        : null,
      open.muted > 0
        ? `${many(open.muted, 'muted alert stays', 'muted alerts stay')} hidden until ${open.muted === 1 ? 'its' : 'their'} mute ends`
        : null,
    ].filter((p): p is string => p !== null);
    return {
      question: `Switch ${label} to Live?`,
      consequence:
        parts.length === 0
          ? 'Nothing is open now. From now on what it finds goes on the alerts board, where you and the office see and work it.'
          : `${parts.join('; ')}. Nobody is notified by the switch.`,
      action: 'Switch to Live',
    };
  }
  const leaving = open.shown + open.snoozed;
  const parts = [
    leaving > 0
      ? `${many(leaving, 'open alert leaves', 'open alerts leave')} the alerts board and ${leaving === 1 ? 'is' : 'are'} shown only here again`
      : null,
    open.muted > 0
      ? `${many(open.muted, 'muted alert was', 'muted alerts were')} hidden from the board already`
      : null,
  ].filter((p): p is string => p !== null);
  return {
    question: `Switch ${label} back to Testing?`,
    consequence:
      parts.length === 0
        ? 'What it finds is shown only here again, for you to judge.'
        : `${parts.join('; ')}.`,
    action: 'Back to Testing',
  };
}

/** The checks' fixed rules (spec §7): text, not settings. */
export function alertRules(): string[] {
  const nightly = MONITOR_KEYS.filter(
    (k) => MONITOR_CATALOG[k].cadence === 'nightly',
  ).map((k) => MONITOR_CATALOG[k].label);
  return [
    `Checked every hour from 7 AM to 7 PM, shop time${
      nightly.length ? `; ${nightly.join(' and ')} once a night, at 6:15 AM` : ''
    }. A monitor that can't see its data says so instead of reporting nothing.`,
    'If nobody takes a Critical alert within 30 minutes, the owner is told; a High one, after 1 business day. Normal alerts never escalate.',
    `Snooze: until tomorrow 7 AM, for 3 days, until Monday 7 AM, or until a date up to ${ALERT_SNOOZE_MAX_DAYS} days ahead.`,
    `Mute one subject for 30 days, 90 days or until a date up to ${ALERT_MUTE_MAX_DAYS} days ahead, with a reason everyone sees. It is still checked, just not shown.`,
    'A resolved alert stays resolved until the problem clears and comes back.',
  ];
}

/** The morning email's status line (spec §7), from the server's yes or no. */
export function morningEmailLine(connected: boolean): string {
  return connected
    ? 'Each morning at 7 AM, the owner and the office get an email of what is waiting.'
    : "Email isn't connected yet — the morning email will start when it is.";
}

export const REVIEW_EXPLAINER = `What each monitor in Testing raised in the last ${ALERT_REVIEW_DAYS} days, cleared or not. Judge each one; switch it to Live when enough are useful.`;

/** "2 of 5 judged useful", and how many are still to judge. */
export function judgedLine(r: { total: number; judged: number; useful: number }): string {
  const head = `${r.useful.toLocaleString('en-US')} of ${r.total.toLocaleString('en-US')} judged useful`;
  const left = r.total - r.judged;
  return left > 0 ? `${head} · ${left.toLocaleString('en-US')} not judged yet` : head;
}

/** Nothing raised in the window: an answer only from a monitor that is being checked. */
export function emptyReviewLine(health: MonitorHealth): string {
  return health.state === 'monitored'
    ? `It raised nothing in the last ${ALERT_REVIEW_DAYS} days.`
    : 'Nothing raised, but this monitor is not being checked right now, so that is not an all-clear.';
}

/** Where one alert in the review stands. */
export function reviewStateLine(
  a: {
    status: string;
    resolvedKind: 'auto' | 'human' | null;
    resolvedAt: Date | string | null;
  },
  tz: string,
  now: Date,
): string {
  if (a.status !== 'resolved') return 'Open';
  const when = a.resolvedAt ? ` ${shopTime(a.resolvedAt, tz, now)}` : '';
  return a.resolvedKind === 'human'
    ? `Resolved by hand${when}`
    : `Cleared by a check${when}`;
}

/** "Until Nov 7 · “Recount on Friday” · Olive Office, Oct 8". */
export function muteLine(
  m: {
    until: Date | string;
    reason: string;
    by: { name: string | null };
    at: Date | string;
  },
  tz: string,
  now: Date,
): string {
  const day = (d: Date | string) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
    }).format(new Date(d));
  return `Until ${day(m.until)} · “${m.reason}” · ${m.by.name ?? 'Someone no longer here'}, ${shopTime(m.at, tz, now)}`;
}

export type Evidence = Record<string, string | number | null>;

const isTicketState = (s: unknown): s is TicketState =>
  typeof s === 'string' && s in TICKET_STATE_LABEL;

function quantityText(q: number): string {
  // A true minus sign: "−2" is a count below zero, "-2" reads as a dash.
  return q < 0 ? `−${Math.abs(q)}` : String(q);
}

/** A date-only value ("2026-10-01") as a day; an instant on the shop's clock. */
function whenText(v: string, tz: string, now: Date): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return dateOnly(v, { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return shopTime(v, tz, now);
}

/**
 * The detail lines the evidence keys of Phase 7's monitors read as (spec §5's evidence), each a
 * sentence, never "key: value". A key outside this list still shows, as "key: value".
 */
const DETAIL: Record<string, (v: string | number, tz: string, now: Date) => string> = {
  receivedAt: (v, tz, now) => `Received ${whenText(String(v), tz, now)}`,
  urgency: (v) => (v === 'emergency' ? 'Emergency' : `${String(v)} urgency`),
  sentAt: (v, tz, now) => `Sent ${whenText(String(v), tz, now)}`,
  lastFollowUpAt: (v, tz, now) => `Last followed up ${whenText(String(v), tz, now)}`,
  lastVisitAt: (v, tz, now) => `Last visit ${whenText(String(v), tz, now)}`,
  overdueSince: (v, tz, now) => `Overdue since ${whenText(String(v), tz, now)}`,
  overdueCount: (v) => (v === 1 ? '1 visit overdue' : `${v} visits overdue`),
  visitStartAt: (v, tz, now) => `Visit ${whenText(String(v), tz, now)}`,
  shortLines: (v) => `Short: ${v}`,
  moreLines: (v) => `and ${v} more`,
  onHand: (v) => `On hand ${v}`,
  onOrder: (v) => `On order ${v}`,
  keepAtLeast: (v) => `Keep at least ${v}`,
  planned: (v) => `Needed ${v}`,
};

/** What a subject is called, before its number (a ticket's number stands alone: "#42"). */
const NUMBERED: Partial<Record<AlertSubjectType, string>> = {
  request: 'Request',
  quote: 'Quote',
  job: 'Job',
};

/**
 * An alert's evidence as lines: a headline (the part, the ticket, the request, the plan…) and
 * the details under it. Written against the evidence KEYS, with the subject's type only to name
 * a number ("Quote #31", where a ticket is "#42"), so a monitor added to the catalog reads
 * without a change here; a key this does not know shows as "key: value" rather than vanishing.
 */
export function evidenceLines(
  evidence: Evidence,
  tz: string,
  now: Date,
  subjectType?: AlertSubjectType,
): { headline: string; details: string[] } {
  const rest = new Map(Object.entries(evidence));
  const take = (k: string) => {
    const v = rest.get(k);
    rest.delete(k);
    return v ?? null;
  };
  const details: string[] = [];
  let headline = '';

  const number = take('number');
  const title = take('title');
  const item = take('item');
  const planName = take('planName');
  const kind = subjectType ? NUMBERED[subjectType] : undefined;
  if (planName !== null) {
    const plan = take('planNumber');
    headline = [String(planName), plan !== null ? `Plan #${plan}` : null]
      .filter(Boolean)
      .join(' · ');
  } else if (kind && number !== null) {
    headline = [`${kind} #${number}`, take('customer')].filter(Boolean).join(' · ');
  } else if (number !== null || title !== null) {
    headline = [number !== null ? `#${number}` : null, title].filter(Boolean).join(' ');
    const status = take('status');
    if (status !== null)
      details.push(isTicketState(status) ? TICKET_STATE_LABEL[status] : String(status));
    const due = take('replyDueAt');
    if (typeof due === 'string') details.push(`Reply was due ${shopTime(due, tz, now)}`);
  } else if (item !== null) {
    headline = String(item);
    const location = take('location');
    if (location !== null) details.push(String(location));
    const quantity = take('quantity');
    if (typeof quantity === 'number') details.push(`Count ${quantityText(quantity)}`);
  } else if (rest.get('customer') != null) {
    headline = String(take('customer'));
  }
  for (const [k, v] of rest) {
    if (v === null) continue;
    // `job_short` always writes `moreLines` (0 when every short line is listed): "and N more"
    // only when there are more, never "and 0 more" (p7 QA).
    if (k === 'moreLines' && !(Number(v) > 0)) continue;
    const say = DETAIL[k];
    details.push(say ? say(v, tz, now) : `${k}: ${v}`);
  }
  return { headline: headline || 'Alert', details };
}
